import { Button, Group, Modal, Stack, Text } from "@mantine/core";
import { SEASON_STATUS_LABEL, formatSeasonDates, type Season } from "./services-queries";

export interface ArchivedSeasonsModalProps {
  readonly opened: boolean;
  readonly seasons: readonly Season[];
  /** services.manage: restore as well as open. */
  readonly canRestore: boolean;
  readonly restoring: boolean;
  readonly onClose: () => void;
  readonly onOpen: (seasonId: string) => void;
  readonly onRestore: (seasonId: string, name: string) => void;
}

/**
 * The seasons taken out of the picker (#321): each can be opened to read its grid, and restored by someone who
 * manages Services. Newest first, as the picker lists seasons.
 */
export function ArchivedSeasonsModal({
  opened,
  seasons,
  canRestore,
  restoring,
  onClose,
  onOpen,
  onRestore,
}: ArchivedSeasonsModalProps) {
  return (
    <Modal opened={opened} onClose={onClose} title="Archived seasons" radius="lg">
      <Stack gap="sm">
        {seasons.length === 0 ? (
          <Text size="sm" c="dimmed">
            No season is archived.
          </Text>
        ) : (
          <Stack gap={0} component="ul" style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {seasons.map((s) => (
              <Group
                key={s.id}
                component="li"
                justify="space-between"
                wrap="wrap"
                gap="xs"
                py="xs"
                data-archived-season={s.id}
              >
                <div>
                  <Text size="sm" fw={600}>
                    {s.name}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {formatSeasonDates(s.startsOn, s.endsOn)} · {SEASON_STATUS_LABEL[s.status]}
                  </Text>
                </div>
                <Group gap="xs">
                  <Button
                    variant="light"
                    size="compact-sm"
                    aria-label={`Open ${s.name}`}
                    onClick={() => {
                      onOpen(s.id);
                    }}
                  >
                    Open
                  </Button>
                  {canRestore ? (
                    <Button
                      variant="subtle"
                      size="compact-sm"
                      aria-label={`Restore ${s.name}`}
                      onClick={() => {
                        if (!restoring) onRestore(s.id, s.name);
                      }}
                    >
                      Restore
                    </Button>
                  ) : null}
                </Group>
              </Group>
            ))}
          </Stack>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
